import { User as UserIcon, UserRoundSearch } from "lucide-react";
import type { User } from "@harmonyso/tenants-sdk";
import type { User as UserType } from "@harmonyso/users-sdk";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ImpersonateDialog } from "@/components/tenants/impersonate-dialog";
import { LoginIdDisplay } from "@/components/tenants/login-id-display";

interface UserTableProps {
  users: User[];
  tenantId: string;
  onImpersonate?: (tenantId: string, loginId: string) => void;
  isImpersonating: boolean;
}

export function UserTable({
  users,
  tenantId,
  onImpersonate,
  isImpersonating,
}: UserTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="bg-muted/50 grid grid-cols-[50px_1fr_1fr_1fr_120px_220px_120px_80px] gap-4 p-4 text-sm font-medium">
        <div></div>
        <div>Name</div>
        <div>Full Name</div>
        <div>Email</div>
        <div>Status</div>
        <div>Login IDs</div>
        <div>Roles</div>
        <div className="text-center">Actions</div>
      </div>

      {users.map((user: User) => {
        const loginIds = user.login_ids ?? [];

        return (
          <div
            key={user.user_id}
            className="hover:bg-muted/20 grid grid-cols-[50px_1fr_1fr_1fr_120px_220px_120px_80px] gap-4 border-t p-4"
          >
            <div className="flex items-center">
              {user.photo_url ? (
                <img
                  src={user.photo_url}
                  alt={user.display_name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-full">
                  <UserIcon className="text-muted-foreground h-4 w-4" />
                </div>
              )}
            </div>

            <div className="flex min-w-0 items-center">
              <p className="truncate font-medium">{user.display_name}</p>
            </div>

            <div className="flex min-w-0 items-center">
              {user.first_name && user.last_name ? (
                <p className="text-muted-foreground truncate text-sm">
                  {user.first_name} {user.last_name}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">—</p>
              )}
            </div>

            <div className="flex min-w-0 items-center">
              <p className="text-muted-foreground truncate text-sm">
                {user.email}
              </p>
            </div>

            <div className="flex items-center">
              <Badge
                variant={user.status === "enabled" ? "default" : "secondary"}
                className={
                  user.status === "enabled"
                    ? "bg-green-600 text-white dark:bg-green-600 dark:text-white"
                    : "bg-red-600 text-white dark:bg-red-600 dark:text-white"
                }
              >
                {user.status}
              </Badge>
            </div>

            <div className="flex min-w-0 items-center overflow-hidden">
              <LoginIdDisplay loginIds={loginIds} userEmail={user.email} />
            </div>

            <div className="flex items-center">
              <div className="flex flex-wrap gap-1">
                {user.roles?.length ? (
                  user.roles.slice(0, 2).map((role) => (
                    <Badge key={role} variant="outline" className="text-xs">
                      {typeof role === "string" ? role : role}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="outline" className="text-xs">
                    User
                  </Badge>
                )}
                {user.roles && user.roles.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{user.roles.length - 2}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center justify-center">
              {onImpersonate && (
                <ImpersonateDialog
                  user={user as UserType}
                  tenantId={tenantId}
                  onConfirm={onImpersonate}
                  trigger={
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex h-8 items-center gap-1 px-3 hover:border-blue-300 hover:bg-blue-50 dark:hover:border-blue-600 dark:hover:bg-blue-950"
                      disabled={isImpersonating}
                      title="Impersonate User"
                    >
                      <UserRoundSearch className="h-4 w-4 text-blue-600" />
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
